import { ArrowLeft } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { listContacts, updateContact } from '@/features/crm/contacts/api';
import { contactKeys } from '@/features/crm/contacts/queries';
import { CompanyForm } from '../components/CompanyForm';
import { useCompany, useCreateCompany, useUpdateCompany } from '../queries';
import type { CompanyInput } from '../types';

export function CompanyFormPage() {
  const { publicId = '' } = useParams();
  const isEdit = Boolean(publicId);
  const companyId = publicId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const companyQuery = useCompany(isEdit ? companyId : '');
  const company = companyQuery.data;
  const create = useCreateCompany();
  const update = useUpdateCompany(companyId);

  // On edit, prefill the picker with the company's current contact (if any).
  const contactsQuery = useQuery({
    queryKey: contactKeys.list({ company: company?.id }),
    queryFn: () => listContacts({ company: company!.id }),
    enabled: isEdit && Boolean(company?.id),
  });
  const existingContactId = contactsQuery.data?.results[0]?.id;

  async function handleSubmit(values: CompanyInput, contactId: string) {
    const saved = isEdit
      ? await update.mutateAsync(values)
      : await create.mutateAsync(values);
    // Link the chosen/created contact to this company (skip if unchanged).
    if (contactId && Number(contactId) !== existingContactId) {
      await updateContact(Number(contactId), { company: saved.id });
      queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
    }
    navigate(`/crm/companies/${saved.public_id}`);
  }

  if (isEdit && (companyQuery.isPending || contactsQuery.isPending)) {
    return (
      <div className="max-w-xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(isEdit ? `/crm/companies/${companyId}` : '/crm/companies')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? `Edit ${company?.name ?? 'company'}` : 'New company'}
        </h1>
      </div>

      <CompanyForm
        submitLabel={isEdit ? 'Save changes' : 'Create company'}
        defaultValues={
          company
            ? {
                name: company.name,
                website: company.website,
                address: company.address,
                country: company.country,
                industry: company.industry,
                technologies: company.technologies,
                notes: company.notes,
                is_active: company.is_active,
                contact: existingContactId ? String(existingContactId) : '',
              }
            : undefined
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate(isEdit ? `/crm/companies/${companyId}` : '/crm/companies')}
      />
    </div>
  );
}
